import { VStack } from '@wegroup/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import LoadingScreen from '../../../../../common/src/components/LoadingScreen';
import { RiskObjects } from '../../../../../parties/src/features/risk-objects/api/getAllRiskObjectByParty';
import { setChosenRiskObjectsForTarif } from '../../../store/redux/slices/offersSlice';
import {
  useLouiseDispatch,
  useLouiseSelector,
} from '../../../store/redux/store';
import CarSelectRadio from './risk-object-radio/CarSelectRadio';
import FamilySelectRadio from './risk-object-radio/FamilySelectRadio';
import HomeSelectRadio from './risk-object-radio/HomeSelectRadio';
import LegalSelectRadio from './risk-object-radio/LegalSelectRadio';
import PartyGroupSelectRadio from './risk-object-radio/PartyGroupSelectRadio';
import TwoWheelerSelectRadio from './risk-object-radio/TwoWheelerSelectRadio';
import RiskObjectSelectRadioGroup from './RiskObjectSelectRadioGroup';

interface Props {
  isLoading: boolean;
  riskObjects?: RiskObjects;
}

const RiskObjectSelectOverview: React.FC<Props> = ({
  isLoading,
  riskObjects,
}) => {
  const { t } = useTranslation();
  const dispatch = useLouiseDispatch();
  const selectedRiskObjects =
    useLouiseSelector((state) => state.offers.chosenRiskObjectsForTarif) || {};

  const handleSelect = (id: string, type: string) => {
    dispatch(setChosenRiskObjectsForTarif({ [type]: id }));
  };

  if (isLoading || !riskObjects) return <LoadingScreen />;

  return (
    <VStack spacing="6" align="stretch">
      {riskObjects?.cars.length > 1 && (
        <RiskObjectSelectRadioGroup
          title={t('CARS')}
          items={riskObjects.cars}
          keyExtractor={(car) => car.risk_object_revision_id as string}
          renderRiskObject={(car) => (
            <CarSelectRadio
              car={car}
              isSelected={
                selectedRiskObjects.car === car.risk_object_revision_id
              }
              onSelect={() =>
                handleSelect(car.risk_object_revision_id as string, 'car')
              }
            />
          )}
        />
      )}
      {riskObjects?.residences.length > 1 && (
        <RiskObjectSelectRadioGroup
          title={t('HOMES')}
          items={riskObjects.residences}
          keyExtractor={(residence) =>
            residence.risk_object_revision_id as string
          }
          renderRiskObject={(residence) => (
            <HomeSelectRadio
              home={residence}
              isSelected={
                selectedRiskObjects.home === residence.risk_object_revision_id
              }
              onSelect={() =>
                handleSelect(
                  residence.risk_object_revision_id as string,
                  'home',
                )
              }
            />
          )}
        />
      )}
      {riskObjects?.legal.length > 1 && (
        <RiskObjectSelectRadioGroup
          title={t('LEGAL')}
          items={riskObjects.legal}
          keyExtractor={(legal) => legal.risk_object_revision_id as string}
          renderRiskObject={(legal) => (
            <LegalSelectRadio
              legal={legal}
              isSelected={
                selectedRiskObjects.family === legal.risk_object_revision_id
              }
              onSelect={() =>
                handleSelect(legal.risk_object_revision_id as string, 'family')
              }
            />
          )}
        />
      )}
      {riskObjects?.families.length > 1 && (
        <RiskObjectSelectRadioGroup
          title={t('FAMILIES')}
          items={riskObjects.families}
          keyExtractor={(family) => family.risk_object_revision_id as string}
          renderRiskObject={(family) => (
            <FamilySelectRadio
              family={family}
              isSelected={
                selectedRiskObjects.family === family.risk_object_revision_id
              }
              onSelect={() =>
                handleSelect(family.risk_object_revision_id as string, 'family')
              }
            />
          )}
        />
      )}
      {riskObjects?.party_groups.length > 1 && (
        <RiskObjectSelectRadioGroup
          title={t('PARTY-GROUPS')}
          items={riskObjects.party_groups}
          keyExtractor={(partyGroup) => partyGroup.risk_object_revision_id}
          renderRiskObject={(partyGroup) => (
            <PartyGroupSelectRadio
              partyGroup={partyGroup}
              isSelected={
                selectedRiskObjects.partyGroup ===
                partyGroup.risk_object_revision_id
              }
              onSelect={() =>
                handleSelect(
                  partyGroup.risk_object_revision_id as string,
                  'partyGroup',
                )
              }
            />
          )}
        />
      )}
      {riskObjects?.two_wheelers.length > 1 && (
        <RiskObjectSelectRadioGroup
          title={t('TWO_WHEELERS')}
          items={riskObjects.two_wheelers}
          keyExtractor={(twoWheeler) => twoWheeler.risk_object_revision_id}
          renderRiskObject={(twoWheeler) => (
            <TwoWheelerSelectRadio
              twoWheeler={twoWheeler}
              isSelected={
                selectedRiskObjects.twoWheeler ===
                twoWheeler.risk_object_revision_id
              }
              onSelect={() =>
                handleSelect(
                  twoWheeler.risk_object_revision_id as string,
                  'twoWheeler',
                )
              }
            />
          )}
        />
      )}
    </VStack>
  );
};

export default RiskObjectSelectOverview;
